import { eur, hrs, fmtDate } from "@/lib/apiClient";
import { Coffee, Pencil } from "lucide-react";
import { RateLines } from "@/components/RateBreakdown";
import { CorrectionHistory } from "@/components/CorrectionHistory";

// Read-only day-by-day breakdown of a timesheet (mirrors the Approvals review layout,
// without any approve/return/edit actions).
export function TimesheetDetail({ ts, onCorrect }) {
  if (!ts) return null;
  const canCorrect = onCorrect && ["approved", "locked", "exported"].includes(ts.status);
  const days = [...new Set((ts.entries || []).map((e) => e.date))].sort();
  return (
    <div data-testid="ts-detail">
      <div className="grid grid-cols-4 gap-3 my-2 text-center">
        <Stat v={hrs(ts.total_paid_hours)} l="Paid hrs" />
        <Stat v={eur(ts.total_wages)} l="Wages" />
        <Stat v={eur(ts.total_charge)} l="Charge" />
        <Stat v={eur(ts.total_cost)} l="Loaded cost" />
      </div>
      <CorrectionHistory items={ts.corrections} />
      {days.map((day) => {
        const de = (ts.entries || []).filter((e) => e.date === day);
        const brk = de.reduce((s, e) => s + (e.auto_break_deducted || 0), 0);
        return (
          <div key={day} className="border border-zinc-200 rounded-md mb-2">
            <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-100 font-bold text-sm num">{fmtDate(day)}</div>
            <div className="p-3 space-y-2">
              {de.map((e, i) => (
                e.kind === "leave" ? (
                  <div key={i} className="text-xs border-b border-zinc-50 pb-2 bg-sky-50/60 rounded px-2 py-1">
                    <div className="flex justify-between font-medium text-sm text-sky-800"><span>{e.bank_holiday ? `Bank Holiday – ${e.bh_name}` : e.leave_paid === false ? "Unpaid leave" : "Annual leave"}</span><span className="num">{hrs(e.paid_hours || e.hours || 0)}h</span></div>
                    <div className="text-sky-600" data-testid={`ts-leave-note-${day}`}>{e.bank_holiday ? `Paid bank holiday${e.labour_cost != null ? ` · ${eur(e.labour_cost)}` : ""} · no site cost` : "Approved leave · no site cost"}</div>
                    {e.bank_holiday && e.labour_cost != null && <RateLines e={e} tid={`ts-rates-${day}-${i}`} />}
                  </div>
                ) : (
                  <div key={i} className="text-xs border-b border-zinc-50 pb-2">
                    <div className="flex justify-between font-medium text-sm"><span>{e.site_name}</span><span className="flex items-center gap-2"><span className="num">{hrs(e.paid_hours)}h</span>{canCorrect && <button data-testid={`correct-entry-${day}-${i}`} onClick={() => onCorrect(ts.entries.indexOf(e))} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-slate-900"><Pencil className="h-3.5 w-3.5" />Correct</button>}</span></div>
                    <div className="num text-zinc-500">{e.start_time}–{e.finish_time}{e.job_type ? ` · ${e.job_type}` : ""}</div>
                    <RateLines e={e} tid={`ts-rates-${day}-${i}`} />
                    {e.description && <div className="text-zinc-400 italic">{e.description}</div>}
                  </div>
                )
              ))}
              {brk > 0 && <div className="text-[11px] text-amber-700 flex items-center gap-1"><Coffee className="h-3 w-3" /> {brk}m break auto-deducted (once)</div>}
            </div>
          </div>
        );
      })}
      {ts.break_overrides?.length > 0 && (
        <div className="text-xs bg-sky-50 border border-sky-200 rounded-md p-2 text-sky-800">
          {ts.break_overrides.map((o, i) => <div key={i}>Break override {fmtDate(o.date)} → {o.break_minutes}m — {o.reason} ({o.by})</div>)}
        </div>
      )}
    </div>
  );
}

function Stat({ v, l }) {
  return <div className="border border-zinc-200 rounded-md p-2"><div className="num text-lg font-bold">{v}</div><div className="text-[10px] uppercase text-zinc-500">{l}</div></div>;
}
